import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Save, TrendingUp, Calculator } from 'lucide-react';
import { ScreenDocButtons } from './ScreenDocButtons';
import { InvestorAppetiteCard } from './InvestorAppetite';

const DEFAULT_YEARS = [
  { year: '2021', revenue: 0, ebitda: 0 },
  { year: '2022', revenue: 0, ebitda: 0 },
  { year: '2023', revenue: 0, ebitda: 0 },
  { year: '2024', revenue: 0, ebitda: 0 },
  { year: '2025E', revenue: 0, ebitda: 0 },
  { year: '2026E', revenue: 0, ebitda: 0 },
];

const fmt = (v) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);


export function FinancialChart({ projectId, financials: initialFinancials, documents, screeningStatus, onScreenStarted, companyWebsite, isAcMember, dealType }) {
  const [data, setData] = useState(initialFinancials?.years || DEFAULT_YEARS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialFinancials?.years?.length) {
      setData(initialFinancials.years);
    }
  }, [initialFinancials]);

  const updateCell = (idx, field, value) => {
    const updated = [...data];
    updated[idx] = { ...updated[idx], [field]: parseFloat(value) || 0 };
    setData(updated);
  };

  const save = async () => {
    setSaving(true);
    try { await api.updateCompanyInfo(projectId, { financials: { years: data } }); toast.success('Financials saved'); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const maxVal = Math.max(...data.map(d => Math.max(d.revenue || 0, d.ebitda || 0)), 0.1);
  const barH = 160;

  return (
    <div className="bg-card border border-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-muted-foreground" />
          <p className="font-heading text-base text-foreground">Key Financials (€m)</p>
        </div>
        <div className="flex items-center gap-2">
          {!isAcMember && (
          <>
          {dealType === 'brokered' && (
          <ScreenDocButtons
            projectId={projectId}
            documents={documents}
            section="financials"
            screening={screeningStatus === 'processing'}
            onStarted={onScreenStarted}
            label="Key Financials"
            companyWebsite={companyWebsite}
          />
          )}
          <Button onClick={save} disabled={saving} className="bg-secondary hover:bg-muted text-secondary-foreground rounded-sm text-[11px] h-7 px-2.5">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
          </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-500" /><span className="text-[10px] text-muted-foreground">Revenue</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-emerald-500" /><span className="text-[10px] text-muted-foreground">EBITDA</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-0.5 bg-amber-400" /><span className="text-[10px] text-muted-foreground">Margin %</span></div>
      </div>

      <BarChart data={data} maxVal={maxVal} barH={barH} />

      <FinancialsTable data={data} onUpdateCell={updateCell} />
    </div>
  );
}


function BarChart({ data, maxVal, barH }) {
  return (
    <div className="relative" style={{ height: barH + 40 }}>
      <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-[9px] text-muted-foreground font-mono text-right pr-1">
        <span>{fmt(maxVal)}</span>
        <span>{fmt(maxVal / 2)}</span>
        <span>0</span>
      </div>
      <div className="ml-11 flex items-end gap-1 sm:gap-3" style={{ height: barH }}>
        {data.map(d => {
          const revH = maxVal > 0 ? ((d.revenue || 0) / maxVal) * barH : 0;
          const ebitdaH = maxVal > 0 ? ((d.ebitda || 0) / maxVal) * barH : 0;
          return (
            <div key={d.year} className="flex-1 flex flex-col items-center justify-end h-full">
              <div className="w-full flex justify-center gap-0.5 items-end" style={{ height: barH }}>
                <div className="w-[40%] bg-blue-500/80 rounded-t-sm transition-all" style={{ height: Math.max(revH, (d.revenue || 0) > 0 ? 2 : 0) }} />
                <div className="w-[40%] bg-emerald-500/80 rounded-t-sm transition-all" style={{ height: Math.max(ebitdaH, (d.ebitda || 0) > 0 ? 2 : 0) }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="ml-11 flex gap-1 sm:gap-3 mt-1">
        {data.map(d => {
          const margin = (d.revenue || 0) > 0 ? (((d.ebitda || 0) / d.revenue) * 100).toFixed(1) : null;
          return (
            <div key={d.year} className="flex-1 text-center">
              {margin && <p className="text-[9px] text-amber-400 font-mono">{margin}%</p>}
              <p className="text-[10px] text-muted-foreground font-mono">{d.year}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function FinancialsTable({ data, onUpdateCell }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[400px]">
        <thead>
          <tr className="text-[10px] text-muted-foreground uppercase tracking-wider">
            <th className="text-left py-1 px-1 w-16">€m</th>
            {data.map(d => <th key={d.year} className="text-center py-1 px-1">{d.year}</th>)}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="text-xs text-blue-400 py-1 px-1">Revenue</td>
            {data.map((d, i) => (
              <td key={d.year} className="py-1 px-1">
                <Input type="number" step="0.1" value={d.revenue || ''} onChange={e => onUpdateCell(i, 'revenue', e.target.value)}
                  className="bg-secondary border-border rounded-sm h-7 text-xs font-mono text-center w-full" />
              </td>
            ))}
          </tr>
          <tr>
            <td className="text-xs text-emerald-400 py-1 px-1">EBITDA</td>
            {data.map((d, i) => (
              <td key={d.year} className="py-1 px-1">
                <Input type="number" step="0.1" value={d.ebitda || ''} onChange={e => onUpdateCell(i, 'ebitda', e.target.value)}
                  className="bg-secondary border-border rounded-sm h-7 text-xs font-mono text-center w-full" />
              </td>
            ))}
          </tr>
          <tr>
            <td className="text-xs text-amber-400 py-1 px-1">Margin</td>
            {data.map(d => (
              <td key={d.year} className="py-1 px-1 text-center text-xs font-mono text-amber-400/70">
                {(d.revenue || 0) > 0 ? (((d.ebitda || 0) / d.revenue) * 100).toFixed(1) + '%' : '-'}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}


function EVBridgeChart({ ev, bankDebt, sellerFinancing, equity }) {
  if (ev <= 0) return null;
  const barH = 140;
  const debtPct = bankDebt / ev;
  const sellerPct = sellerFinancing / ev;
  const eqPct = equity / ev;
  return (
    <div className="flex items-end gap-1 sm:gap-3 justify-center" style={{ height: barH + 40 }}>
      <div className="flex flex-col items-center w-20">
        <div className="w-full rounded-t-sm bg-foreground/15 flex items-end justify-center" style={{ height: barH }}>
          <span className="font-mono text-xs font-bold text-foreground pb-1">€{fmt(ev)}m</span>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1">EV</p>
      </div>
      <div className="flex items-center pb-6 text-muted-foreground text-lg font-bold">−</div>
      <div className="flex flex-col items-center w-20">
        <div className="relative w-full" style={{ height: barH }}>
          <div className="absolute top-0 left-0 right-0 bg-blue-500/40 rounded-t-sm flex items-center justify-center" style={{ height: Math.max(debtPct * barH, bankDebt > 0 ? 14 : 0) }}>
            {bankDebt > 0 && <span className="font-mono text-[10px] font-bold text-blue-300">€{fmt(bankDebt)}m</span>}
          </div>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1">Bank</p>
      </div>
      <div className="flex items-center pb-6 text-muted-foreground text-lg font-bold">−</div>
      <div className="flex flex-col items-center w-20">
        <div className="relative w-full" style={{ height: barH }}>
          <div className="absolute left-0 right-0 bg-purple-500/40 flex items-center justify-center" style={{ top: Math.max(debtPct * barH, bankDebt > 0 ? 14 : 0), height: Math.max(sellerPct * barH, sellerFinancing > 0 ? 14 : 0) }}>
            {sellerFinancing > 0 && <span className="font-mono text-[10px] font-bold text-purple-300">€{fmt(sellerFinancing)}m</span>}
          </div>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1">Seller</p>
      </div>
      <div className="flex items-center pb-6 text-foreground text-lg font-bold">=</div>
      <div className="flex flex-col items-center w-20">
        <div className="w-full flex flex-col justify-end" style={{ height: barH }}>
          <div className="bg-green-500/40 rounded-t-sm w-full flex items-center justify-center" style={{ height: Math.max(eqPct * barH, equity > 0 ? 14 : 0) }}>
            <span className="font-mono text-xs font-bold text-green-300">€{fmt(equity)}m</span>
          </div>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1">Equity</p>
      </div>
    </div>
  );
}


function SourcesSummaryTable({ ev, bankDebt, sellerFinancing, equity, baseEbitda }) {
  function pct(val) { return ev > 0 ? ((val / ev) * 100).toFixed(1) : '0.0'; }
  function xEbitda(val) { return baseEbitda > 0 ? fmt(val / baseEbitda) : '-'; }
  return (
    <div className="mt-3 border border-border rounded-sm overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-secondary/50 text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left py-1.5 px-3">Source</th>
            <th className="text-right py-1.5 px-3">€m</th>
            <th className="text-right py-1.5 px-3">% of EV</th>
            <th className="text-right py-1.5 px-3">x EBITDA</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-border">
            <td className="py-1.5 px-3 text-foreground font-medium">Enterprise Value</td>
            <td className="py-1.5 px-3 text-right font-mono text-foreground">{fmt(ev)}</td>
            <td className="py-1.5 px-3 text-right font-mono text-muted-foreground">100.0%</td>
            <td className="py-1.5 px-3 text-right font-mono text-muted-foreground">{xEbitda(ev)}x</td>
          </tr>
          <tr className="border-t border-border">
            <td className="py-1.5 px-3 text-blue-400">Bank Debt</td>
            <td className="py-1.5 px-3 text-right font-mono text-blue-400">{fmt(bankDebt)}</td>
            <td className="py-1.5 px-3 text-right font-mono text-blue-400/70">{pct(bankDebt)}%</td>
            <td className="py-1.5 px-3 text-right font-mono text-blue-400/70">{xEbitda(bankDebt)}x</td>
          </tr>
          <tr className="border-t border-border">
            <td className="py-1.5 px-3 text-purple-400">Seller Financing</td>
            <td className="py-1.5 px-3 text-right font-mono text-purple-400">{fmt(sellerFinancing)}</td>
            <td className="py-1.5 px-3 text-right font-mono text-purple-400/70">{pct(sellerFinancing)}%</td>
            <td className="py-1.5 px-3 text-right font-mono text-purple-400/70">{xEbitda(sellerFinancing)}x</td>
          </tr>
          <tr className="border-t-2 border-border">
            <td className="py-1.5 px-3 text-green-400 font-bold">Equity Required</td>
            <td className="py-1.5 px-3 text-right font-mono text-green-400 font-bold">{fmt(equity)}</td>
            <td className="py-1.5 px-3 text-right font-mono text-green-400/70">{pct(equity)}%</td>
            <td className="py-1.5 px-3 text-right font-mono text-green-400/70">{xEbitda(equity)}x</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}


function AssumptionsForm({ multiple, setMultiple, manualEbitda, setManualEbitda, latestEbitda, bankDebt, setBankDebt, sellerFinancing, setSellerFinancing, sfType, setSfType }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-foreground/70 uppercase tracking-wider">Assumptions</p>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground w-28 shrink-0">EBITDA (€m)</label>
        <Input type="number" step="0.1" value={manualEbitda !== null ? manualEbitda : latestEbitda || ''}
          onChange={e => setManualEbitda(e.target.value)}
          placeholder={latestEbitda ? `${fmt(latestEbitda)} (from CIM)` : 'Enter EBITDA'}
          className="bg-secondary border-border rounded-sm h-7 text-xs font-mono flex-1" />
        {latestEbitda > 0 && manualEbitda !== null && <button onClick={() => setManualEbitda(null)} className="text-[10px] text-muted-foreground hover:text-foreground">Reset</button>}
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground w-28 shrink-0">EV / EBITDA</label>
        <Input type="number" step="0.1" value={multiple} onChange={e => setMultiple(parseFloat(e.target.value) || 0)}
          className="bg-secondary border-border rounded-sm h-7 text-xs font-mono flex-1" />
        <span className="text-xs text-muted-foreground">x</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground w-28 shrink-0">Bank Debt (€m)</label>
        <Input type="number" step="0.1" value={bankDebt} onChange={e => setBankDebt(parseFloat(e.target.value) || 0)}
          className="bg-secondary border-border rounded-sm h-7 text-xs font-mono flex-1" />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground w-28 shrink-0">Seller Fin. (€m)</label>
        <Input type="number" step="0.1" value={sellerFinancing} onChange={e => setSellerFinancing(parseFloat(e.target.value) || 0)}
          className="bg-secondary border-border rounded-sm h-7 text-xs font-mono flex-1" />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground w-28 shrink-0">SF Type</label>
        <Select value={sfType} onValueChange={setSfType}>
          <SelectTrigger className="bg-secondary border-border rounded-sm h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border rounded-sm">
            <SelectItem value="vendor_loan" className="text-xs">Vendor Loan</SelectItem>
            <SelectItem value="earn_out" className="text-xs">Earn-Out</SelectItem>
            <SelectItem value="seller_rollover" className="text-xs">Seller Rollover</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}


export function SourcesUsesCalculator({ projectId, financials, sourcesUses: initialSU, isAcMember, investorAppetite }) {
  const years = financials?.years || [];
  const historicalEbitda = years.filter(d => !String(d.year).includes('E') && (d.ebitda || 0) > 0);
  const latestEbitda = historicalEbitda.length > 0 ? historicalEbitda[historicalEbitda.length - 1].ebitda : 0;

  const [multiple, setMultiple] = useState(initialSU?.multiple || 5);
  const [manualEbitda, setManualEbitda] = useState(initialSU?.manual_ebitda || null);
  const [bankDebt, setBankDebt] = useState(initialSU?.bank_debt || 0);
  const [sellerFinancing, setSellerFinancing] = useState(initialSU?.seller_financing || 0);
  const [sfType, setSfType] = useState(initialSU?.sf_type || 'vendor_loan');
  const [tickets, setTickets] = useState(initialSU?.tickets || 1);
  const [saving, setSaving] = useState(false);

  const baseEbitda = manualEbitda !== null && manualEbitda !== '' ? parseFloat(manualEbitda) || 0 : latestEbitda;
  const ev = baseEbitda * multiple;
  const equity = Math.max(ev - bankDebt - sellerFinancing, 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateCompanyInfo(projectId, { sources_uses: { multiple, manual_ebitda: manualEbitda, bank_debt: bankDebt, seller_financing: sellerFinancing, sf_type: sfType, tickets } });
      toast.success('Saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <>
    <div className="bg-card border border-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-muted-foreground" />
          <p className="font-heading text-base text-foreground">Sources & Uses (€m)</p>
        </div>
        {!isAcMember && (
        <Button onClick={save} disabled={saving} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-sm text-[11px] h-7 px-2.5">
          <Save className="w-3 h-3 mr-1" /> Save
        </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AssumptionsForm multiple={multiple} setMultiple={setMultiple} manualEbitda={manualEbitda} setManualEbitda={setManualEbitda}
          latestEbitda={latestEbitda} bankDebt={bankDebt} setBankDebt={setBankDebt} sellerFinancing={sellerFinancing}
          setSellerFinancing={setSellerFinancing} sfType={sfType} setSfType={setSfType} />

        <div className="space-y-3">
          <p className="text-xs text-foreground/70 uppercase tracking-wider">EV to Equity Bridge</p>
          <EVBridgeChart ev={ev} bankDebt={bankDebt} sellerFinancing={sellerFinancing} equity={equity} />
          <SourcesSummaryTable ev={ev} bankDebt={bankDebt} sellerFinancing={sellerFinancing} equity={equity} baseEbitda={baseEbitda} />
        </div>
      </div>
    </div>

    <EquityTicketCalc equity={equity} tickets={tickets} setTickets={setTickets} />
    <InvestorAppetiteCard projectId={projectId} appetite={investorAppetite} equity={equity} />
    </>
  );
}


function EquityTicketCalc({ equity, tickets, setTickets }) {
  const ticketPct = tickets > 0 ? (tickets / 20) * 100 : 0;
  const ticketAmount = equity * (tickets / 20);
  return (
    <div className="bg-card border border-border rounded-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-foreground/60" />
        <p className="font-heading text-base text-foreground">Equity Ticket Calculator</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Tickets</label>
          <Input type="number" min="0.1" max="20" step="0.1" value={tickets} onChange={e => setTickets(Math.min(20, Math.max(0, parseFloat(e.target.value) || 0)))}
            className="bg-secondary border-border rounded-sm h-7 text-xs font-mono w-20" />
          <span className="text-xs text-muted-foreground">/ 20</span>
        </div>
        <span className="text-xs text-muted-foreground">=</span>
        <div className="bg-green-950/20 border border-green-900/30 rounded-sm px-3 py-1.5">
          <span className="font-mono text-lg font-bold text-green-400">€{fmt(ticketAmount)}m</span>
          <span className="text-xs text-green-400/70 ml-2">({ticketPct.toFixed(1)}%)</span>
        </div>
      </div>
      <div className="mt-2 flex gap-2 flex-wrap">
        {[0.5, 1, 1.5, 2, 3, 5, 10].map(t => (
          <button key={t} onClick={() => setTickets(t)}
            className={`text-xs px-2 py-1 rounded-sm border transition-colors ${tickets === t ? 'bg-green-600/15 border-green-600/30 text-green-400' : 'border-border text-muted-foreground hover:text-foreground'}`}>
            {t}x = €{fmt(equity * (t / 20))}m ({((t / 20) * 100).toFixed(1)}%)
          </button>
        ))}
      </div>
    </div>
  );
}
